import { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { BookOpen, GraduationCap, Send, Sparkles, Network } from "lucide-react";
import { toast } from "sonner";
import KnowledgeGraph from "@/components/KnowledgeGraph";

const PATHS = [
  { id: "path_creator", title: "Becoming an AI Creator", lessons: 8, color: "#00E5FF" },
  { id: "path_market",  title: "Marketplace Mastery", lessons: 6, color: "#F5C542" },
  { id: "path_design",  title: "Design for Living Interfaces", lessons: 5, color: "#7B2CBF" },
];

export default function KnowledgeZone() {
  const [items, setItems] = useState([]);
  const [topic, setTopic] = useState(null);
  const [msgs, setMsgs] = useState([
    { role: "assistant", text: "Pick a topic on the left, then ask anything." },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    api.get("/knowledge").then((r) => setItems(r.data));
  }, []);
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [msgs]);

  const ask = async () => {
    if (!input.trim() || busy) return;
    const text = input.trim();
    setInput("");
    setMsgs((m) => [...m, { role: "user", text }]);
    setBusy(true);
    try {
      const r = await api.post("/ai/chat", {
        message: text,
        provider: "gemini",
        session_id: sessionId || undefined,
        system: `You are an AI Tutor in the NEXUS Knowledge Layer${topic ? `, currently teaching: ${topic}` : ""}. Cite reasoning. Use short sections with headings if helpful.`,
      });
      setSessionId(r.data.session_id);
      setMsgs((m) => [...m, { role: "assistant", text: r.data.reply }]);
    } catch { toast.error("Tutor unreachable"); }
    setBusy(false);
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-[#00E5FF] mb-1">knowledge layer</div>
        <h1 className="font-display text-4xl font-bold tracking-tight">Verified intelligence, live tutors</h1>
        <p className="text-[#64748B] text-sm mt-1">Graph-curated topics with personalized AI tutors.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Knowledge graph + paths */}
        <div className="lg:col-span-7 space-y-6">
          <div className="glass-heavy rounded-3xl p-5">
            <div className="flex items-center gap-2 mb-2">
              <Network size={18} className="text-[#00E5FF]" />
              <div className="font-display font-bold">Knowledge graph</div>
              <div className="ml-auto text-xs text-[#64748B]">Click a node to focus the tutor</div>
            </div>
            {items.length > 0 && (
              <KnowledgeGraph
                items={items}
                selected={topic}
                onSelect={(t) => { setTopic(t); toast.success(`Tutor focused on: ${t}`); }}
              />
            )}
          </div>

          <div className="glass-heavy rounded-3xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <BookOpen size={18} className="text-[#00E5FF]" />
              <div className="font-display font-bold">Curated topics</div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {items.map((k, i) => (
                <motion.button
                  key={k.id}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  onClick={() => { setTopic(k.title); toast.success(`Tutor focused on: ${k.title}`); }}
                  data-testid={`topic-${k.id}`}
                  className={`text-left glass rounded-2xl p-4 transition-all hover:-translate-y-0.5 ${topic === k.title ? "glow-ring" : ""}`}
                >
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#F5C542]">{k.topic}</div>
                  <div className="font-display font-semibold mt-1">{k.title}</div>
                  <div className="text-xs text-[#64748B] mt-1">{k.summary}</div>
                </motion.button>
              ))}
            </div>
          </div>

          <div className="glass-heavy rounded-3xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <GraduationCap size={18} className="text-[#F5C542]" />
              <div className="font-display font-bold">Personalized learning paths</div>
            </div>
            <div className="space-y-3">
              {PATHS.map((p) => (
                <div key={p.id} className="glass rounded-2xl p-4 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: `${p.color}22`, border: `1px solid ${p.color}55` }}>
                    <Sparkles size={18} style={{ color: p.color }} />
                  </div>
                  <div className="flex-1">
                    <div className="font-display font-semibold">{p.title}</div>
                    <div className="text-xs text-[#64748B]">{p.lessons} adaptive lessons</div>
                  </div>
                  <button className="nexus-btn-secondary text-xs !py-1.5 !px-3">Enter</button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* AI tutor */}
        <div className="lg:col-span-5 glass-heavy rounded-3xl p-5 flex flex-col" style={{ minHeight: 560 }}>
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={18} className="text-[#00E5FF]" />
            <div className="font-display font-bold">AI Tutor</div>
            {topic && <span className="ml-2 text-xs glass px-2 py-1 rounded-full text-[#00E5FF]">{topic}</span>}
          </div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-2 pr-1">
            {msgs.map((m, i) => (
              <div key={`${m.role}-${i}-${(m.text || "").slice(0, 12)}`} className={`text-sm rounded-2xl px-3 py-2 ${
                m.role === "user"
                  ? "bg-[#00E5FF]/10 border border-[#00E5FF]/30 ml-6"
                  : "glass mr-6"
              }`}>{m.text}</div>
            ))}
            {busy && <div className="text-xs text-[#64748B]">reasoning…</div>}
          </div>
          <div className="mt-3 flex gap-2">
            <input
              data-testid="tutor-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && ask()}
              placeholder="Ask the tutor…"
              className="nexus-input"
            />
            <button data-testid="tutor-send" onClick={ask} disabled={busy} className="nexus-btn-primary !py-2 !px-3"><Send size={14} /></button>
          </div>
        </div>
      </div>
    </div>
  );
}
